import {
  cloneState,
  numberField,
  stringField,
  type EntityType,
  type FrameworkId,
  type RelationType,
  type SchemaField,
  type SyncrelEntity,
  type SyncrelRelation,
  type SyncrelState,
} from "./types";
import {
  FRAMEWORK_CANVAS_FIELD,
  entityFrameworkCanvas,
  scopeLegacyEntitiesToFramework,
} from "./framework-scope";
import { migrateLegacyFieldReferences } from "./state-migrations";

export interface FrameworkTemplate {
  id: FrameworkId;
  label: string;
  shortLabel: string;
  category: "Prioritization" | "Discovery" | "Strategy";
  description: string;
  fields: SchemaField[];
}

const riceFields: SchemaField[] = [
  { key: "reach", label: "Reach", type: "number", description: "Estimated users or accounts reached." },
  { key: "impact", label: "Impact", type: "number", description: "0.25, 0.5, 1, 2, or 3 impact multiplier." },
  { key: "confidence", label: "Confidence", type: "percent", description: "Confidence percentage from 0 to 100." },
  { key: "effort", label: "Effort", type: "number", description: "Person-weeks or equivalent sizing unit." },
  { key: "valueScore", label: "Value", type: "computed", readonly: true },
  { key: "riceScore", label: "RICE Score", type: "computed", readonly: true },
];

const iceFields: SchemaField[] = [
  { key: "impact", label: "Impact", type: "number", description: "Expected business or customer impact." },
  { key: "confidence", label: "Confidence", type: "percent", description: "Confidence percentage from 0 to 100." },
  { key: "ease", label: "Ease", type: "number", description: "How easy the work is to ship." },
  { key: "iceScore", label: "ICE Score", type: "computed", readonly: true },
];

const valueEffortFields: SchemaField[] = [
  { key: "priorityValue", label: "Value", type: "number" },
  { key: "effort", label: "Effort", type: "number" },
  { key: "valueEffortQuadrant", label: "Quadrant", type: "select", options: ["Quick win", "Major bet", "Fill-in", "Avoid"] },
];

const weightedScoringFields: SchemaField[] = [
  { key: "strategicFit", label: "Strategic Fit", type: "number" },
  { key: "customerValue", label: "Customer Value", type: "number" },
  { key: "deliveryConfidence", label: "Delivery Confidence", type: "percent" },
  { key: "weightedScore", label: "Weighted Score", type: "computed", readonly: true },
];

const wsjfFields: SchemaField[] = [
  { key: "userBusinessValue", label: "User-Business Value", type: "number" },
  { key: "timeCriticality", label: "Time Criticality", type: "number" },
  { key: "riskReduction", label: "Risk Reduction / Opportunity", type: "number" },
  { key: "jobSize", label: "Job Size", type: "number" },
  { key: "wsjfScore", label: "WSJF Score", type: "computed", readonly: true },
];

const costOfDelayFields: SchemaField[] = [
  { key: "costOfDelay", label: "Cost of Delay", type: "currency" },
  { key: "duration", label: "Duration", type: "number" },
  { key: "cd3Score", label: "CD3 Score", type: "computed", readonly: true },
];

const moscowFields: SchemaField[] = [
  { key: "moscowPriority", label: "MoSCoW Priority", type: "select", options: ["Must", "Should", "Could", "Won't"] },
  { key: "priorityReason", label: "Priority Reason", type: "text" },
];

const opportunityScoringFields: SchemaField[] = [
  { key: "opportunityImportance", label: "Importance", type: "number" },
  { key: "currentSatisfaction", label: "Current Satisfaction", type: "number" },
  { key: "opportunityGap", label: "Opportunity Gap", type: "computed", readonly: true },
];

const eisenhowerFields: SchemaField[] = [
  { key: "urgency", label: "Urgency", type: "select", options: ["Urgent", "Not urgent"] },
  { key: "importance", label: "Importance", type: "select", options: ["Important", "Not important"] },
  { key: "eisenhowerQuadrant", label: "Quadrant", type: "select", options: ["Do", "Schedule", "Delegate", "Delete"] },
];

const impactEffortFields: SchemaField[] = [
  { key: "impactScore", label: "Impact", type: "number" },
  { key: "effort", label: "Effort", type: "number" },
  { key: "impactEffortQuadrant", label: "Quadrant", type: "select", options: ["Quick win", "Big bet", "Fill-in", "Time sink"] },
];

const buyAFeatureFields: SchemaField[] = [
  { key: "featureCost", label: "Feature Cost", type: "currency" },
  { key: "budgetVotes", label: "Budget Votes", type: "number" },
  { key: "budgetRemaining", label: "Budget Remaining", type: "currency" },
  { key: "voteRationale", label: "Vote Rationale", type: "text" },
];

const stackRankingFields: SchemaField[] = [
  { key: "rank", label: "Rank", type: "number" },
  { key: "rankRationale", label: "Rank Rationale", type: "text" },
];

const piePxlFields: SchemaField[] = [
  { key: "piePotential", label: "Potential", type: "number" },
  { key: "pieImportance", label: "Importance", type: "number" },
  { key: "pieEase", label: "Ease", type: "number" },
  { key: "evidenceLevel", label: "Evidence Level", type: "select", options: ["Low", "Medium", "High"] },
  { key: "pieScore", label: "PIE / PXL Score", type: "computed", readonly: true },
];

const storyMapFields: SchemaField[] = [
  { key: "persona", label: "User Persona", type: "text" },
  { key: "backboneStep", label: "Backbone Step", type: "text" },
  { key: "releaseSlice", label: "Release Slice", type: "select", options: ["MVP", "Release 1.1", "Future"] },
  { key: "storyMapRank", label: "Story Map Rank", type: "number" },
];

const revenueFields: SchemaField[] = [
  { key: "contractValue", label: "Contract Value", type: "currency" },
  { key: "clientUrgency", label: "Client Urgency", type: "select", options: ["High", "Medium", "Low"] },
  { key: "enterpriseAccount", label: "Enterprise Account", type: "boolean" },
  { key: "atRiskAccount", label: "At-Risk Account", type: "boolean" },
  { key: "weightedPipelineValue", label: "Weighted Pipeline Value", type: "computed", readonly: true },
];

const opportunitySolutionFields: SchemaField[] = [
  { key: "desiredOutcome", label: "Desired Outcome", type: "text" },
  { key: "opportunity", label: "Opportunity", type: "text" },
  { key: "solutionBet", label: "Solution Bet", type: "text" },
  { key: "assumptionRisk", label: "Assumption Risk", type: "select", options: ["High", "Medium", "Low"] },
  { key: "evidenceStrength", label: "Evidence Strength", type: "select", options: ["Weak", "Moderate", "Strong"] },
];

const kanoFields: SchemaField[] = [
  { key: "kanoCategory", label: "Kano Category", type: "select", options: ["Must-have", "Performance", "Delighter", "Indifferent"] },
  { key: "satisfaction", label: "Satisfaction", type: "number" },
  { key: "dissatisfaction", label: "Dissatisfaction", type: "number" },
  { key: "kanoConfidence", label: "Kano Confidence", type: "percent" },
];

const jobsFields: SchemaField[] = [
  { key: "jobStatement", label: "Job Statement", type: "text" },
  { key: "situation", label: "Situation", type: "text" },
  { key: "motivation", label: "Motivation", type: "text" },
  { key: "expectedOutcome", label: "Expected Outcome", type: "text" },
];

const impactMapFields: SchemaField[] = [
  { key: "actor", label: "Actor", type: "text" },
  { key: "behaviorChange", label: "Behavior Change", type: "text" },
  { key: "impactMetric", label: "Impact Metric", type: "text" },
  { key: "deliverableConfidence", label: "Deliverable Confidence", type: "percent" },
];

const northStarFields: SchemaField[] = [
  { key: "northStarMetric", label: "North Star Metric", type: "text" },
  { key: "inputMetric", label: "Input Metric", type: "text" },
  { key: "metricImpact", label: "Metric Impact", type: "number" },
  { key: "metricConfidence", label: "Metric Confidence", type: "percent" },
];

const okrFields: SchemaField[] = [
  { key: "objective", label: "Objective", type: "text" },
  { key: "keyResult", label: "Key Result", type: "text" },
  { key: "krProgress", label: "KR Progress", type: "percent" },
  { key: "betConfidence", label: "Bet Confidence", type: "percent" },
];

const gistFields: SchemaField[] = [
  { key: "gistGoal", label: "Goal", type: "text" },
  { key: "idea", label: "Idea", type: "text" },
  { key: "stepProject", label: "Step Project", type: "text" },
  { key: "task", label: "Task", type: "text" },
];

const heartFields: SchemaField[] = [
  { key: "happiness", label: "Happiness", type: "number" },
  { key: "engagement", label: "Engagement", type: "number" },
  { key: "adoption", label: "Adoption", type: "number" },
  { key: "retention", label: "Retention", type: "number" },
  { key: "taskSuccess", label: "Task Success", type: "number" },
];

const aarrrFields: SchemaField[] = [
  { key: "funnelStage", label: "AARRR Stage", type: "select", options: ["Acquisition", "Activation", "Retention", "Referral", "Revenue"] },
  { key: "funnelMetric", label: "Funnel Metric", type: "text" },
  { key: "conversionImpact", label: "Conversion Impact", type: "number" },
];

const experimentFields: SchemaField[] = [
  { key: "hypothesis", label: "Hypothesis", type: "text" },
  { key: "experimentType", label: "Experiment Type", type: "select", options: ["Prototype", "Concierge", "A/B", "Interview"] },
  { key: "successMetric", label: "Success Metric", type: "text" },
  { key: "learningStatus", label: "Learning Status", type: "select", options: ["Queued", "Running", "Learned", "Stopped"] },
];

const nowNextLaterFields: SchemaField[] = [
  { key: "roadmapHorizon", label: "Roadmap Horizon", type: "select", options: ["Now", "Next", "Later"] },
  { key: "horizonRationale", label: "Horizon Rationale", type: "text" },
];

const threeHorizonsFields: SchemaField[] = [
  { key: "portfolioHorizon", label: "Portfolio Horizon", type: "select", options: ["H1: Core", "H2: Emerging", "H3: Transformational"] },
  { key: "portfolioBet", label: "Portfolio Bet", type: "text" },
];

const leanCanvasFields: SchemaField[] = [
  { key: "problem", label: "Problem", type: "text" },
  { key: "customerSegment", label: "Customer Segment", type: "text" },
  { key: "valueProposition", label: "Value Proposition", type: "text" },
  { key: "channel", label: "Channel", type: "text" },
  { key: "revenueStream", label: "Revenue Stream", type: "text" },
];

const valuePropositionCanvasFields: SchemaField[] = [
  { key: "customerJobs", label: "Customer Jobs", type: "text" },
  { key: "pains", label: "Pains", type: "text" },
  { key: "gains", label: "Gains", type: "text" },
  { key: "productsServices", label: "Products & Services", type: "text" },
  { key: "painRelievers", label: "Pain Relievers", type: "text" },
  { key: "gainCreators", label: "Gain Creators", type: "text" },
];

const swotFields: SchemaField[] = [
  { key: "swotQuadrant", label: "SWOT Quadrant", type: "select", options: ["Strength", "Weakness", "Opportunity", "Threat"] },
  { key: "swotSignal", label: "Signal", type: "text" },
];

const fiveForcesFields: SchemaField[] = [
  { key: "force", label: "Force", type: "select", options: ["Rivalry", "New Entrants", "Suppliers", "Buyers", "Substitutes"] },
  { key: "forceStrength", label: "Force Strength", type: "select", options: ["Low", "Medium", "High"] },
  { key: "strategicResponse", label: "Strategic Response", type: "text" },
];

const impactRelations: RelationType[] = [
  { key: "drives", label: "drives", inverseLabel: "driven by" },
  { key: "impacts", label: "impacts", inverseLabel: "impacted by" },
  { key: "resolved-by", label: "resolved by", inverseLabel: "resolves" },
  { key: "signal-origin", label: "signal origin", inverseLabel: "originates signal" },
  { key: "blocked-by", label: "blocked by", inverseLabel: "blocks" },
];

export const frameworkCatalog: FrameworkTemplate[] = [
  {
    id: "rice",
    label: "RICE scoring",
    shortLabel: "RICE",
    category: "Prioritization",
    description: "Reach, impact, confidence, effort, and computed priority score.",
    fields: riceFields,
  },
  {
    id: "ice",
    label: "ICE scoring",
    shortLabel: "ICE",
    category: "Prioritization",
    description: "Impact, confidence, ease, and a lightweight priority score.",
    fields: iceFields,
  },
  {
    id: "value-effort",
    label: "Value vs. Effort",
    shortLabel: "Value/Effort",
    category: "Prioritization",
    description: "Value, effort, and quadrant mapping for tradeoff decisions.",
    fields: valueEffortFields,
  },
  {
    id: "weighted-scoring",
    label: "Weighted Scoring",
    shortLabel: "Weighted",
    category: "Prioritization",
    description: "Strategic fit, customer value, confidence, and weighted score.",
    fields: weightedScoringFields,
  },
  {
    id: "wsjf",
    label: "Weighted Shortest Job First",
    shortLabel: "WSJF",
    category: "Prioritization",
    description: "Cost of delay components divided by job size.",
    fields: wsjfFields,
  },
  {
    id: "cost-of-delay",
    label: "Cost of Delay / CD3",
    shortLabel: "CD3",
    category: "Prioritization",
    description: "Cost of delay, duration, and CD3 sequencing score.",
    fields: costOfDelayFields,
  },
  {
    id: "moscow",
    label: "MoSCoW prioritization",
    shortLabel: "MoSCoW",
    category: "Prioritization",
    description: "Must, should, could, and won't priority buckets.",
    fields: moscowFields,
  },
  {
    id: "kano",
    label: "Kano model",
    shortLabel: "Kano",
    category: "Prioritization",
    description: "Must-have, performance, delighter, and satisfaction signals.",
    fields: kanoFields,
  },
  {
    id: "opportunity-scoring",
    label: "Opportunity Scoring",
    shortLabel: "ODI",
    category: "Prioritization",
    description: "Importance, current satisfaction, and unmet opportunity gap.",
    fields: opportunityScoringFields,
  },
  {
    id: "eisenhower",
    label: "Eisenhower Matrix",
    shortLabel: "Eisenhower",
    category: "Prioritization",
    description: "Urgency and importance quadrants for action planning.",
    fields: eisenhowerFields,
  },
  {
    id: "impact-effort",
    label: "Impact vs. Effort",
    shortLabel: "Impact/Effort",
    category: "Prioritization",
    description: "Impact, effort, and quadrant classification.",
    fields: impactEffortFields,
  },
  {
    id: "buy-a-feature",
    label: "Buy-a-Feature / Priority Poker",
    shortLabel: "Buy Feature",
    category: "Prioritization",
    description: "Budget votes, feature cost, remaining budget, and rationale.",
    fields: buyAFeatureFields,
  },
  {
    id: "stack-ranking",
    label: "Stack Ranking",
    shortLabel: "Stack Rank",
    category: "Prioritization",
    description: "Forced rank order with explicit rationale.",
    fields: stackRankingFields,
  },
  {
    id: "pie-pxl",
    label: "PIE / PXL scoring",
    shortLabel: "PIE/PXL",
    category: "Prioritization",
    description: "Potential, importance, ease, evidence, and score.",
    fields: piePxlFields,
  },
  {
    id: "impact-mapping",
    label: "Impact mapping",
    shortLabel: "Impact",
    category: "Discovery",
    description: "Actors, behavior changes, impact metrics, and delivery confidence.",
    fields: impactMapFields,
  },
  {
    id: "story-map",
    label: "User Story Map",
    shortLabel: "Story Map",
    category: "Discovery",
    description: "Persona, backbone step, release slice, and journey rank.",
    fields: storyMapFields,
  },
  {
    id: "opportunity-solution-tree",
    label: "Opportunity Solution Tree",
    shortLabel: "OST",
    category: "Discovery",
    description: "Outcomes, opportunities, solution bets, assumptions, and evidence.",
    fields: opportunitySolutionFields,
  },
  {
    id: "jobs-to-be-done",
    label: "Jobs to be Done",
    shortLabel: "JTBD",
    category: "Discovery",
    description: "Job statement, situation, motivation, and expected outcome.",
    fields: jobsFields,
  },
  {
    id: "north-star",
    label: "North Star Metric",
    shortLabel: "North Star",
    category: "Discovery",
    description: "North star, input metrics, impact, and confidence.",
    fields: northStarFields,
  },
  {
    id: "okrs",
    label: "OKRs",
    shortLabel: "OKRs",
    category: "Discovery",
    description: "Objectives, key results, progress, and confidence.",
    fields: okrFields,
  },
  {
    id: "gist",
    label: "GIST planning",
    shortLabel: "GIST",
    category: "Discovery",
    description: "Goals, ideas, step projects, and tasks.",
    fields: gistFields,
  },
  {
    id: "heart",
    label: "HEART metrics",
    shortLabel: "HEART",
    category: "Discovery",
    description: "Happiness, engagement, adoption, retention, and task success.",
    fields: heartFields,
  },
  {
    id: "aarrr",
    label: "AARRR funnel",
    shortLabel: "AARRR",
    category: "Discovery",
    description: "Funnel stage, metric, and conversion impact.",
    fields: aarrrFields,
  },
  {
    id: "experiment",
    label: "Experiment design",
    shortLabel: "Experiment",
    category: "Discovery",
    description: "Hypothesis, experiment type, success metric, and learning status.",
    fields: experimentFields,
  },
  {
    id: "revenue",
    label: "Revenue assignment",
    shortLabel: "Revenue",
    category: "Strategy",
    description: "Contract value, urgency, enterprise flags, and weighted pipeline.",
    fields: revenueFields,
  },
  {
    id: "now-next-later",
    label: "Now / Next / Later",
    shortLabel: "NNL",
    category: "Strategy",
    description: "Roadmap horizon and rationale for sequencing.",
    fields: nowNextLaterFields,
  },
  {
    id: "three-horizons",
    label: "Three Horizons",
    shortLabel: "Horizons",
    category: "Strategy",
    description: "Core, emerging, and transformational portfolio bets.",
    fields: threeHorizonsFields,
  },
  {
    id: "lean-canvas",
    label: "Lean Canvas / Business Model Canvas",
    shortLabel: "Canvas",
    category: "Strategy",
    description: "Problem, segment, value proposition, channel, and revenue model.",
    fields: leanCanvasFields,
  },
  {
    id: "value-proposition-canvas",
    label: "Value Proposition Canvas",
    shortLabel: "VPC",
    category: "Strategy",
    description: "Customer jobs, pains, gains, products, relievers, and creators.",
    fields: valuePropositionCanvasFields,
  },
  {
    id: "swot",
    label: "SWOT analysis",
    shortLabel: "SWOT",
    category: "Strategy",
    description: "Strengths, weaknesses, opportunities, threats, and signals.",
    fields: swotFields,
  },
  {
    id: "five-forces",
    label: "Porter's Five Forces",
    shortLabel: "5 Forces",
    category: "Strategy",
    description: "Competitive force, strength, and strategic response.",
    fields: fiveForcesFields,
  },
];

const frameworkById = new Map(frameworkCatalog.map((framework) => [framework.id, framework]));

export const frameworkTemplate = (id: FrameworkId): FrameworkTemplate => (
  frameworkById.get(id) ?? frameworkCatalog[0]
);

export const enabledFrameworks = (state: SyncrelState): FrameworkTemplate[] => {
  const ids: FrameworkId[] = state.workspace.enabledFrameworks?.length
    ? state.workspace.enabledFrameworks
    : state.workspace.activeFramework
      ? [state.workspace.activeFramework]
      : ["rice"];
  return uniqueFrameworkIds(ids).map((id) => frameworkTemplate(id));
};

export class FrameworkManager {
  applyTemplate(state: SyncrelState, templateId: FrameworkId): SyncrelState {
    const template = frameworkTemplate(templateId);
    const legacyScope = state.workspace.activeFramework ?? templateId;
    const prepared = ensureFrameworkCanvas(
      scopeLegacyEntitiesToFramework(state, legacyScope),
      template,
    );
    if (templateId === "rice") return this.applyRice(prepared);
    if (templateId === "story-map") return this.applyStoryMap(prepared);
    if (templateId === "revenue") return this.applyRevenue(prepared);
    return this.applySchemaOnly(prepared, templateId);
  }

  enableTemplate(state: SyncrelState, templateId: FrameworkId): SyncrelState {
    return this.applyTemplate(state, templateId);
  }

  removeTemplate(state: SyncrelState, templateId: FrameworkId): SyncrelState {
    const next = cloneState(state);
    const remaining = uniqueFrameworkIds(
      (next.workspace.enabledFrameworks ?? []).filter((id) => id !== templateId),
    );
    const activeFramework = next.workspace.activeFramework === templateId
      ? remaining[0] ?? null
      : next.workspace.activeFramework;
    return {
      ...next,
      workspace: {
        ...next.workspace,
        activeFramework,
        enabledFrameworks: remaining,
        updatedAt: Date.now(),
      },
    };
  }

  private applyRice(state: SyncrelState): SyncrelState {
    const next = this.withSchema(state, riceFields, impactRelations);
    const scopedEntities = next.entities.filter((entity) => entityFrameworkCanvas(entity) === "rice");
    const maxValue = Math.max(
      1,
      ...scopedEntities.map((entity) => {
        const reach = numberField(entity.fields, "reach");
        const impact = numberField(entity.fields, "impact", 1);
        const confidence = numberField(entity.fields, "confidence", 100) / 100;
        return reach * impact * confidence;
      }),
    );

    next.entities = next.entities.map((entity) => {
      if (entityFrameworkCanvas(entity) !== "rice") return entity;
      if (entity.type !== "feature" && entity.type !== "deliverable") return entity;
      const reach = numberField(entity.fields, "reach");
      const impact = numberField(entity.fields, "impact", 1);
      const confidence = numberField(entity.fields, "confidence", 100) / 100;
      const effort = Math.max(1, numberField(entity.fields, "effort", 1));
      const valueScore = Math.round(reach * impact * confidence);
      const riceScore = Math.round((valueScore / effort) * 100) / 100;
      const effortRatio = Math.min(1, effort / 40);
      const valueRatio = Math.min(1, valueScore / maxValue);

      return {
        ...entity,
        fields: {
          ...entity.fields,
          reach,
          impact,
          confidence: Math.round(confidence * 100),
          effort,
          valueScore,
          riceScore,
        },
        position: {
          x: 180 + valueRatio * 560,
          y: 560 - effortRatio * 340,
        },
      };
    });

    return this.stamp(next, "rice");
  }

  private applyStoryMap(state: SyncrelState): SyncrelState {
    const next = this.withSchema(state, storyMapFields, impactRelations);
    const scopedEntities = next.entities.filter((entity) => entityFrameworkCanvas(entity) === "story-map");
    const configuredSteps = unique(scopedEntities.map((entity) => stringField(entity.fields, "backboneStep", "")));
    const steps = configuredSteps.length > 1 ? configuredSteps : ["Discover", "Evaluate", "Adopt"];
    const releases = ["MVP", "Release 1.1", "Future"];
    let scopedIndex = 0;

    next.entities = next.entities.map((entity) => {
      if (entityFrameworkCanvas(entity) !== "story-map") return entity;
      const index = scopedIndex;
      scopedIndex += 1;
      const defaultStep = steps[index % steps.length] ?? "Discover";
      const defaultRelease = releases[Math.floor(index / steps.length) % releases.length] ?? "MVP";
      const step = stringField(entity.fields, "backboneStep", defaultStep);
      const release = stringField(entity.fields, "releaseSlice", defaultRelease);
      const stepIndex = Math.max(0, steps.indexOf(step));
      const releaseIndex = Math.max(0, releases.indexOf(release));
      const stackIndex = Math.floor(index / (steps.length * releases.length));
      return {
        ...entity,
        fields: {
          persona: "Product manager",
          storyMapRank: index + 1,
          ...entity.fields,
          backboneStep: step,
          releaseSlice: release,
        },
        doc: entity.doc.trim()
          ? entity.doc
          : `# ${entity.name}\n\n## User Story\nAs a product team, we want ${entity.name.toLowerCase()} so customer value is visible across strategy, planning, and delivery.\n\n## Acceptance Notes\n- Linked to a journey step\n- Assigned to a release slice\n- Has a clear owner before handoff\n`,
        position: {
          x: 160 + stepIndex * 260 + stackIndex * 34,
          y: 180 + releaseIndex * 190 + stackIndex * 28,
        },
      };
    });

    return this.stamp(next, "story-map");
  }

  private applyRevenue(state: SyncrelState): SyncrelState {
    const next = this.withSchema(state, revenueFields, impactRelations);
    let scopedIndex = 0;

    next.entities = next.entities.map((entity) => {
      if (entityFrameworkCanvas(entity) !== "revenue") return entity;
      const index = scopedIndex;
      scopedIndex += 1;
      const contractValue = numberField(entity.fields, "contractValue");
      const urgency = stringField(entity.fields, "clientUrgency", "Medium");
      const urgencyMultiplier = urgency === "High" ? 1.2 : 1;
      const signalWeight = Math.max(1, numberField(entity.fields, "impactScore", 1));
      const weightedPipelineValue = Math.round(contractValue * urgencyMultiplier * signalWeight);
      return {
        ...entity,
        fields: {
          enterpriseAccount: false,
          atRiskAccount: urgency === "High",
          ...entity.fields,
          contractValue,
          clientUrgency: urgency,
          weightedPipelineValue,
        },
        position: {
          x: 180 + (index % 3) * 280,
          y: 180 + Math.floor(index / 3) * 180,
        },
      };
    });

    const revenueEntities = next.entities
      .filter((entity) => entityFrameworkCanvas(entity) === "revenue")
      .sort((a, b) => numberField(b.fields, "weightedPipelineValue") - numberField(a.fields, "weightedPipelineValue"));
    const otherEntities = next.entities.filter((entity) => entityFrameworkCanvas(entity) !== "revenue");
    next.entities = [...revenueEntities, ...otherEntities];
    return this.stamp(next, "revenue");
  }

  private applySchemaOnly(state: SyncrelState, templateId: FrameworkId): SyncrelState {
    const template = frameworkTemplate(templateId);
    const next = this.withSchema(state, template.fields, impactRelations);
    let scopedIndex = 0;
    next.entities = next.entities.map((entity) => {
      if (entityFrameworkCanvas(entity) !== templateId) return entity;
      const index = scopedIndex;
      scopedIndex += 1;
      return {
        ...entity,
        fields: {
          ...defaultFields(templateId, index),
          ...entity.fields,
        },
      };
    });
    return this.stamp(next, templateId);
  }

  private withSchema(state: SyncrelState, fields: SchemaField[], relationTypes: RelationType[]): SyncrelState {
    const next = cloneState(migrateLegacyFieldReferences(state));
    next.schema.fields = mergeFields(next.schema.fields, fields);
    next.schema.relationTypes = mergeRelations(next.schema.relationTypes, relationTypes);
    return next;
  }

  private stamp(state: SyncrelState, templateId: FrameworkId): SyncrelState {
    const selectedEntityId = state.entities.find((entity) => entityFrameworkCanvas(entity) === templateId)?.id ?? null;
    return {
      ...state,
      workspace: {
        ...state.workspace,
        activeFramework: templateId,
        enabledFrameworks: uniqueFrameworkIds([
          ...(state.workspace.enabledFrameworks ?? []),
          templateId,
        ]),
        selectedEntityId,
        selectedRelationId: null,
        updatedAt: Date.now(),
      },
    };
  }
}

const defaultFields = (templateId: FrameworkId, index: number): Record<string, unknown> => {
  if (templateId === "ice") {
    return {
      impact: 2,
      confidence: 70,
      ease: 3,
      iceScore: 14,
    };
  }
  if (templateId === "value-effort") {
    return {
      priorityValue: 5,
      effort: 3,
      valueEffortQuadrant: "Quick win",
    };
  }
  if (templateId === "weighted-scoring") {
    return {
      strategicFit: 4,
      customerValue: 4,
      deliveryConfidence: 70,
      weightedScore: 8,
    };
  }
  if (templateId === "wsjf") {
    return {
      userBusinessValue: 8,
      timeCriticality: 5,
      riskReduction: 3,
      jobSize: 5,
      wsjfScore: 3.2,
    };
  }
  if (templateId === "cost-of-delay") {
    return {
      costOfDelay: 25000,
      duration: 4,
      cd3Score: 6250,
    };
  }
  if (templateId === "moscow") {
    return {
      moscowPriority: ["Must", "Should", "Could"][index % 3],
      priorityReason: "Clarify why this belongs in this bucket.",
    };
  }
  if (templateId === "kano") {
    return {
      kanoCategory: ["Must-have", "Performance", "Delighter"][index % 3],
      satisfaction: 3,
      dissatisfaction: 2,
      kanoConfidence: 70,
    };
  }
  if (templateId === "opportunity-scoring") {
    return {
      opportunityImportance: 8,
      currentSatisfaction: 4,
      opportunityGap: 4,
    };
  }
  if (templateId === "eisenhower") {
    return {
      urgency: index % 2 === 0 ? "Urgent" : "Not urgent",
      importance: "Important",
      eisenhowerQuadrant: index % 2 === 0 ? "Do" : "Schedule",
    };
  }
  if (templateId === "impact-effort") {
    return {
      impactScore: 7,
      effort: 3,
      impactEffortQuadrant: "Quick win",
    };
  }
  if (templateId === "buy-a-feature") {
    return {
      featureCost: 100,
      budgetVotes: 0,
      budgetRemaining: 1000,
      voteRationale: "Stakeholder buying rationale.",
    };
  }
  if (templateId === "stack-ranking") {
    return {
      rank: index + 1,
      rankRationale: "Why this item sits here.",
    };
  }
  if (templateId === "pie-pxl") {
    return {
      piePotential: 7,
      pieImportance: 7,
      pieEase: 5,
      evidenceLevel: "Medium",
      pieScore: 19,
    };
  }
  if (templateId === "opportunity-solution-tree") {
    return {
      desiredOutcome: "Increase customer value",
      opportunity: "Unmet customer need",
      solutionBet: "Candidate solution",
      assumptionRisk: "Medium",
      evidenceStrength: "Moderate",
    };
  }
  if (templateId === "jobs-to-be-done") {
    return {
      jobStatement: "When..., I want..., so I can...",
      situation: "Customer workflow",
      motivation: "Progress sought",
      expectedOutcome: "Measurable improvement",
    };
  }
  if (templateId === "impact-mapping") {
    return {
      actor: "Customer",
      behaviorChange: "Adopt the workflow",
      impactMetric: "Activation",
      deliverableConfidence: 70,
    };
  }
  if (templateId === "north-star") {
    return {
      northStarMetric: "Weekly valuable actions",
      inputMetric: "Activation events",
      metricImpact: 3,
      metricConfidence: 70,
    };
  }
  if (templateId === "okrs") {
    return {
      objective: "Improve customer outcomes",
      keyResult: "Move the target metric",
      krProgress: 0,
      betConfidence: 70,
    };
  }
  if (templateId === "gist") {
    return {
      gistGoal: "Grow the product outcome",
      idea: "Candidate idea",
      stepProject: "Smallest useful project",
      task: "Next action",
    };
  }
  if (templateId === "heart") {
    return {
      happiness: 3,
      engagement: 3,
      adoption: 3,
      retention: 3,
      taskSuccess: 3,
    };
  }
  if (templateId === "aarrr") {
    return {
      funnelStage: ["Acquisition", "Activation", "Retention", "Referral", "Revenue"][index % 5],
      funnelMetric: "Conversion rate",
      conversionImpact: 3,
    };
  }
  if (templateId === "experiment") {
    return {
      hypothesis: "We believe this will improve the target metric.",
      experimentType: "Prototype",
      successMetric: "Validated learning",
      learningStatus: "Queued",
    };
  }
  if (templateId === "now-next-later") {
    return {
      roadmapHorizon: ["Now", "Next", "Later"][index % 3],
      horizonRationale: "Sequence based on value, risk, and readiness.",
    };
  }
  if (templateId === "three-horizons") {
    return {
      portfolioHorizon: ["H1: Core", "H2: Emerging", "H3: Transformational"][index % 3],
      portfolioBet: "Portfolio bet to validate.",
    };
  }
  if (templateId === "lean-canvas") {
    return {
      problem: "Customer problem",
      customerSegment: "Primary segment",
      valueProposition: "Unique value proposition",
      channel: "Primary channel",
      revenueStream: "Revenue model",
    };
  }
  if (templateId === "value-proposition-canvas") {
    return {
      customerJobs: "Functional, social, or emotional job",
      pains: "Current friction",
      gains: "Desired gain",
      productsServices: "Relevant product or service",
      painRelievers: "How this reduces pain",
      gainCreators: "How this creates gain",
    };
  }
  if (templateId === "swot") {
    return {
      swotQuadrant: ["Strength", "Weakness", "Opportunity", "Threat"][index % 4],
      swotSignal: "Evidence or signal.",
    };
  }
  if (templateId === "five-forces") {
    return {
      force: ["Rivalry", "New Entrants", "Suppliers", "Buyers", "Substitutes"][index % 5],
      forceStrength: "Medium",
      strategicResponse: "Strategic response.",
    };
  }
  return {};
};

const mergeFields = (existing: SchemaField[], incoming: SchemaField[]): SchemaField[] => {
  const map = new Map(existing.map((field) => [field.key, field]));
  for (const field of incoming) map.set(field.key, { ...map.get(field.key), ...field });
  return Array.from(map.values());
};

const mergeRelations = (existing: RelationType[], incoming: RelationType[]): RelationType[] => {
  const map = new Map(existing.map((relation) => [relation.key, relation]));
  for (const relation of incoming) map.set(relation.key, { ...map.get(relation.key), ...relation });
  return Array.from(map.values());
};

const unique = (values: string[]): string[] => {
  const seen = values.filter(Boolean);
  return seen.length ? Array.from(new Set(seen)) : ["Discover", "Evaluate", "Adopt"];
};

const uniqueFrameworkIds = (ids: FrameworkId[]): FrameworkId[] => {
  const valid = ids.filter((id) => frameworkById.has(id));
  return Array.from(new Set(valid));
};

const ensureFrameworkCanvas = (state: SyncrelState, template: FrameworkTemplate): SyncrelState => {
  const hasCanvasContent = state.entities.some((entity) => entityFrameworkCanvas(entity) === template.id);
  if (hasCanvasContent) return state;

  const now = Date.now();
  const usedIds = new Set(state.entities.map((entity) => entity.id));
  const blueprints = starterBlueprints(template);
  const entities = blueprints.map((blueprint, index): SyncrelEntity => {
    return {
      id: uniqueStarterEntityId(usedIds, template.id, blueprint.role),
      type: blueprint.type,
      name: blueprint.name,
      status: blueprint.status,
      fields: {
        ...defaultFields(template.id, index),
        ...blueprint.fields,
        [FRAMEWORK_CANVAS_FIELD]: template.id,
        frameworkStarterFramework: template.id,
        frameworkStarterRole: blueprint.role,
      },
      doc: starterDoc(template, blueprint),
      position: blueprint.position,
      createdAt: now,
      updatedAt: now,
    };
  });
  const relations = starterRelations(template.id, entities, now);

  return {
    ...state,
    entities: [...state.entities, ...entities],
    relations: [
      ...state.relations.filter((relation) => !relations.some((item) => item.id === relation.id)),
      ...relations,
    ],
    workspace: {
      ...state.workspace,
      updatedAt: now,
    },
  };
};

type StarterRole = "outcome" | "segment" | "baseline" | "lever" | "bet";

interface StarterBlueprint {
  role: StarterRole;
  type: EntityType;
  name: string;
  status: SyncrelEntity["status"];
  fields: Record<string, unknown>;
  position: { x: number; y: number };
  why: string;
  next: string[];
  poll: string;
}

const starterRoleFields = (
  template: FrameworkTemplate,
  role: StarterRole,
): Record<string, unknown> => {
  if (template.id !== "rice") return {};
  const riceFieldsByRole: Partial<Record<StarterRole, Record<string, unknown>>> = {
    baseline: {
      reach: 1800,
      impact: 1,
      confidence: 70,
      effort: 4,
    },
    lever: {
      reach: 5200,
      impact: 2,
      confidence: 75,
      effort: 12,
    },
    bet: {
      reach: 2400,
      impact: 3,
      confidence: 60,
      effort: 24,
    },
  };
  return riceFieldsByRole[role] ?? {};
};

const starterBlueprints = (template: FrameworkTemplate): StarterBlueprint[] => {
  if (template.id === "kano") return kanoStarterBlueprints();

  const lens = template.shortLabel;
  const fieldList = template.fields.slice(0, 4).map((field) => field.label).join(", ");
  return [
    {
      role: "outcome",
      type: "goal",
      name: `${lens} outcome to protect`,
      status: "active",
      fields: starterRoleFields(template, "outcome"),
      position: { x: 150, y: 150 },
      why: `Define the outcome this ${template.label} canvas should improve before scoring any ideas.`,
      next: [
        "Replace the sample wording with one measurable product or business outcome.",
        "Write the decision deadline and who owns it.",
        `Use ${fieldList || "the framework fields"} as the evidence checklist.`,
      ],
      poll: "Which outcome would make this framework worth using this week?",
    },
    {
      role: "segment",
      type: "actor",
      name: `${lens} customer segment`,
      status: "active",
      fields: starterRoleFields(template, "segment"),
      position: { x: 420, y: 150 },
      why: "A framework is only useful when the customer, buyer, user, or stakeholder lens is explicit.",
      next: [
        "Name the segment and the moment where they feel the problem.",
        "Add the strongest qualitative or quantitative signal you already have.",
        "Connect this segment to each candidate card that depends on it.",
      ],
      poll: "Which segment should shape this decision first?",
    },
    {
      role: "baseline",
      type: "feature",
      name: `${lens} baseline bet`,
      status: "active",
      fields: starterRoleFields(template, "baseline"),
      position: { x: 150, y: 360 },
      why: "Start with the safest viable bet so the canvas has an anchor, not an empty brainstorming prompt.",
      next: [
        "Replace this sample with your real product idea.",
        "Fill every framework field with a rough first estimate.",
        "Write the smallest version that could create evidence in one sprint.",
      ],
      poll: "Is this baseline bet good enough to compare against?",
    },
    {
      role: "lever",
      type: "feature",
      name: `${lens} high-leverage alternative`,
      status: "active",
      fields: starterRoleFields(template, "lever"),
      position: { x: 420, y: 360 },
      why: "A good starter canvas gives you a meaningful contrast immediately.",
      next: [
        "Make this a bolder alternative to the baseline card.",
        "Use the fields to show why it might win or lose.",
        "Capture the riskiest assumption before anyone starts building.",
      ],
      poll: "Does this alternative deserve discovery time?",
    },
    {
      role: "bet",
      type: "deliverable",
      name: `${lens} decision memo`,
      status: "active",
      fields: starterRoleFields(template, "bet"),
      position: { x: 690, y: 360 },
      why: "Turn the canvas into an actual decision artifact while the reasoning is fresh.",
      next: [
        "Summarize the recommended bet.",
        "List what would change the decision.",
        "Open the spec panel and turn this into a reader-ready memo.",
      ],
      poll: "Inline poll to add: Which option should we commit to next?",
    },
  ];
};

const kanoStarterBlueprints = (): StarterBlueprint[] => [
  {
    role: "outcome",
    type: "goal",
    name: "Kano outcome to protect",
    status: "active",
    fields: { kanoConfidence: 75 },
    position: { x: 150, y: 150 },
    why: "Kano works best when the team names the customer emotion it refuses to break.",
    next: [
      "Write the customer promise that must remain true.",
      "Choose the product moment where satisfaction or frustration is easiest to observe.",
      "Use this as the anchor for every must-have, performance, and delighter card.",
    ],
    poll: "Which customer emotion matters most in this workflow?",
  },
  {
    role: "segment",
    type: "actor",
    name: "Kano customer segment",
    status: "active",
    fields: { kanoConfidence: 70 },
    position: { x: 420, y: 150 },
    why: "The same feature can be a delighter for one segment and table stakes for another.",
    next: [
      "Replace this with the exact user or buyer segment.",
      "Add the trigger moment where expectations are formed.",
      "Attach interview notes, tickets, or usage signals before scoring.",
    ],
    poll: "Which segment should classify these expectations?",
  },
  {
    role: "baseline",
    type: "feature",
    name: "Kano must-have baseline",
    status: "active",
    fields: {
      kanoCategory: "Must-have",
      satisfaction: 1,
      dissatisfaction: 5,
      kanoConfidence: 80,
    },
    position: { x: 150, y: 360 },
    why: "Must-haves rarely create delight, but missing them destroys trust.",
    next: [
      "Replace this sample with your real product idea.",
      "Describe the failure state customers will not forgive.",
      "Add the evidence that proves this is table stakes.",
    ],
    poll: "Would users consider this broken if it were missing?",
  },
  {
    role: "lever",
    type: "feature",
    name: "Kano performance lever",
    status: "active",
    fields: {
      kanoCategory: "Performance",
      satisfaction: 4,
      dissatisfaction: 3,
      kanoConfidence: 65,
    },
    position: { x: 420, y: 360 },
    why: "Performance features create a visible more-is-better tradeoff.",
    next: [
      "Name the measurable slider customers care about.",
      "Set a minimum acceptable level and a remarkable level.",
      "Capture what cost or complexity rises with better performance.",
    ],
    poll: "How much better does this need to be before users notice?",
  },
  {
    role: "bet",
    type: "deliverable",
    name: "Kano delighter bet",
    status: "active",
    fields: {
      kanoCategory: "Delighter",
      satisfaction: 5,
      dissatisfaction: 1,
      kanoConfidence: 55,
    },
    position: { x: 690, y: 360 },
    why: "Delighters are bets on surprise, so they need a tight validation loop.",
    next: [
      "Write the smallest prototype that could create delight.",
      "Decide what signal proves the delight is real.",
      "Open the spec panel and ask readers to vote before build starts.",
    ],
    poll: "Inline poll to add: Which delighter should we prototype first?",
  },
];

const uniqueStarterEntityId = (
  usedIds: Set<string>,
  frameworkId: FrameworkId,
  role: StarterRole,
): string => {
  const seed = `starter-${frameworkId}-${role}`;
  if (!usedIds.has(seed)) {
    usedIds.add(seed);
    return seed;
  }
  let index = 2;
  while (usedIds.has(`${seed}-${index}`)) index += 1;
  const id = `${seed}-${index}`;
  usedIds.add(id);
  return id;
};

const starterRelations = (
  frameworkId: FrameworkId,
  entities: SyncrelEntity[],
  now: number,
): SyncrelRelation[] => {
  const byRole = new Map(entities.map((entity) => [entity.fields.frameworkStarterRole, entity]));
  const links: Array<{ from: StarterRole; to: StarterRole; type: string; label: string }> = [
    { from: "outcome", to: "segment", type: "drives", label: "frames" },
    { from: "segment", to: "baseline", type: "signal-origin", label: "expects" },
    { from: "segment", to: "lever", type: "signal-origin", label: "values" },
    { from: "lever", to: "bet", type: "drives", label: "informs" },
  ];
  return links.flatMap((link) => {
    const from = byRole.get(link.from);
    const to = byRole.get(link.to);
    if (!from || !to) return [];
    return [{
      id: `rel-starter-${frameworkId}-${link.from}-${link.to}`,
      fromId: from.id,
      toId: to.id,
      type: link.type,
      label: link.label,
      createdAt: now,
    }];
  });
};

const starterDoc = (
  template: FrameworkTemplate,
  blueprint: StarterBlueprint,
): string => (
  `# ${blueprint.name}\n\n` +
  `## How to use this starter canvas\n` +
  `This is a high-quality starting point for ${template.label}. Replace the sample wording with your real product, service, workflow, or decision.\n\n` +
  `## Why it matters\n${blueprint.why}\n\n` +
  `## Do next\n${blueprint.next.map((item) => `- ${item}`).join("\n")}\n\n` +
  `## Reader feedback\n${blueprint.poll}\n`
);
